import { createRootRouteWithContext, Outlet, useLocation, useNavigate } from '@tanstack/react-router'
import { TanStackRouterDevtools } from '@tanstack/react-router-devtools'
import { QueryClient } from '@tanstack/react-query'
import { Icon } from '@iconify/react'
import { useAuth } from '../../contexts/AuthContext'
import { LoginPage } from '../../components/Auth/LoginPage'
import BrainiacsLoader from '../../components/atoms/BrainiacsLoader'

// Router context type - includes query client and auth
export interface RouterContext {
  queryClient: QueryClient
  auth: {
    user: ReturnType<typeof useAuth>['currentUser']
    loading: boolean
    isAdmin: boolean
  }
}

// Routes that don't require authentication
const PUBLIC_ROUTES = ['/auth/callback', '/auth/magic-link', '/login', '/register']

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  // Root-level error boundary: without this, a render crash (e.g. the
  // Chrome-Translate DOM-mutation "insertBefore" crash) unmounts the whole
  // tree with only a console warning. The router recovers on navigation —
  // no full page reload needed.
  errorComponent: RootError,
})

/**
 * Full-screen fallback for uncaught route errors. Offers a soft recovery
 * (navigate to campaigns, which remounts the tree) before the hard option
 * (full reload).
 */
function RootError({ error }: { error: Error }) {
  const navigate = useNavigate()
  const isDomMutationError = /insertBefore|removeChild/i.test(error.message || '')

  return (
    <div className="min-h-dvh bg-[#050505] flex items-center justify-center p-8">
      <div className="max-w-md w-full glass-panel rounded-3xl p-8 text-center">
        <div className="w-16 h-16 rounded-full bg-red-500/10 flex items-center justify-center mx-auto mb-6">
          <Icon icon="bx:error-circle" className="w-8 h-8 text-red-400" aria-hidden="true" />
        </div>
        <h1 className="text-xl font-bold text-white mb-2">Algo salió mal</h1>
        {isDomMutationError ? (
          <p className="text-sm text-white/55 mb-6">
            La vista no pudo actualizarse (normalmente lo causa la traducción
            automática del navegador manipulando la página). Vuelve a entrar a
            la sección — tus datos no se han perdido.
          </p>
        ) : (
          <p className="text-sm text-white/55 mb-6">
            Se produjo un error inesperado. Vuelve a entrar a la sección o
            recarga la página.
          </p>
        )}
        <div className="text-xs text-white/40 mb-6 p-3 bg-red-500/5 rounded-xl text-left overflow-auto max-h-32 break-words">
          {error.message || 'Unknown error'}
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate({ to: '/campaigns' })}
            className="flex-1 px-6 py-3 bg-[#5260FF] text-white rounded-xl hover:bg-[#5260FF]/90 transition-colors font-medium"
          >
            Volver a campañas
          </button>
          <button
            onClick={() => window.location.reload()}
            className="flex-1 px-6 py-3 bg-white/[0.06] border border-white/10 text-white/80 rounded-xl hover:bg-white/[0.1] transition-colors font-medium"
          >
            Recargar página
          </button>
        </div>
      </div>
    </div>
  )
}

function RootLayout() {
  "use memo";
  const { currentUser, loading } = useAuth()
  const location = useLocation()

  // Check if current route is public (doesn't require auth)
  const isPublicRoute = PUBLIC_ROUTES.some(route => location.pathname.startsWith(route))

  // Show loading state while auth is initializing (but not for public routes).
  // Same branded loader the campaigns routes use, so the boot → route handoff
  // never jumps between two spinner styles. The fullscreen variant owns its
  // own bg + min-h-dvh centering.
  if (loading && !isPublicRoute) {
    return (
      <BrainiacsLoader
        variant="fullscreen"
        size="lg"
        message="Connecting to Brainiacs AI Engine"
        subtitle="Checking your session and loading the latest account data."
      />
    )
  }

  // Allow public routes without authentication
  if (isPublicRoute) {
    return <Outlet />
  }

  // Show login page if not authenticated
  if (!currentUser) {
    return <LoginPage />
  }

  // Render authenticated content
  return (
    <>
      <style>{`
        .root-content-fade { animation: root-content-fade-in 0.6s ease-out; }
        @keyframes root-content-fade-in {
          0% { opacity: 0; }
          100% { opacity: 1; }
        }
      `}</style>
      <div className="root-content-fade">
        <Outlet />
      </div>
      {process.env.NODE_ENV === 'development' && (
        <TanStackRouterDevtools position="bottom-right" />
      )}
    </>
  )
}
